/**
 * Dedicated resident planner, calm and minimal (spec 2026-10-14): the picked day is the headline; a
 * week strip (phone) or the always-open month (desktop) picks it; Overdue folds into one line; the
 * day's to-dos end in "New to-do". Same CRUD as the home widget (shared TodoFormModal, same
 * useResidentTodos seam — RTK Query dedupes the fetch between surfaces). One responsive component for
 * both navigators (mobile 'ResidentPlanner', desktop 'Planner'). State model: a single
 * `selectedDate`. The strip shows its week and the month its month; ‹ › jump to the 1st of the
 * adjacent month (so day clamping can't happen), a swipe moves it a week, and Today brings it back.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import {
  CHECKBOX,
  DayTitle,
  Fold,
  MonthGrid,
  NewTodoRow,
  OverdueSection,
  PlannerWeekStrip,
  ROW_PAD_LARGE,
  TodoRow,
  TodoFormModal,
  insertNowLine,
  type TodoFormValues,
} from '@/components/residents/redesign/todoCalendar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { useResidentTodos } from '@/hooks/residents/useResidentTodos';
import { useMinuteClock } from '@/hooks/shared/useMinuteClock';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import {
  addDays,
  addMonthsToStart,
  getMonthMatrix,
  groupTodosByDateKey,
  parseDateKey,
  sortTodos,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import { formatTime, isLate, nextTimedTodo, nowLineIndex, toTimeKey } from '@/utils/residents/todoTime';
import type { EditScope, ResidentTodo } from '@/types/residents/todos';

const HP = REDESIGN_SPACING.horizontalPadding;
// Match the payments/events desktop content frame.
const MAX_CONTENT_WIDTH = 1100;
/** Desktop: the always-open month's column. */
const MONTH_COLUMN_WIDTH = 360;
/**
 * 14 per side reaches the 44pt target around ~17pt text on native. react-native-web ignores hitSlop,
 * so on web the target is the text box: accepted for this rare state.
 */
const RETRY_SLOP = { top: 14, bottom: 14, left: 14, right: 14 };
/**
 * The list doesn't clip and has room around it (the title block above, New to-do below), so the now
 * line sits ON the boundary at its ends instead of inset into the first or last row.
 */
const NOW_LINE_OPTIONS = { insetEdges: false };

interface PlannerScreenProps {
  /** Test seam: pins "now" — its date AND its time of day. Defaults to the device clock, re-read by the minute. */
  todayOverride?: Date;
}

export const PlannerScreen: React.FC<PlannerScreenProps> = ({ todayOverride }) => {
  const route = useRoute();
  const showBackLink =
    (route.params as { showBackLink?: boolean } | undefined)?.showBackLink === true;
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const reduceMotion = useReducedMotion();

  // "Now", by the minute: a passed time turns late, and the Overdue cutoff rolls over at midnight.
  const now = useMinuteClock(todayOverride);
  const [selectedDate, setSelectedDate] = useState<Date>(now);
  // Phones: the date line swaps the week strip for the month. Closed on every visit.
  const [monthOpen, setMonthOpen] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [formTodo, setFormTodo] = useState<ResidentTodo | null>(null);

  const todayKey = toDateKey(now);
  const nowTimeKey = toTimeKey(now);
  const selectedKey = toDateKey(selectedDate);
  // The month grid's own cells, spill-over days included: the hook expands repeats over exactly this
  // range. Keyed on the month — any day of it draws the same grid, and the picked day's whole week
  // sits inside it.
  const monthKey = selectedKey.slice(0, 7);
  const range = useMemo(() => {
    const weeks = getMonthMatrix(parseDateKey(`${monthKey}-01`));
    return { from: toDateKey(weeks[0][0]), to: toDateKey(weeks[weeks.length - 1][6]) };
  }, [monthKey]);
  const {
    todos,
    overdueTodos,
    toggleTodo,
    isLoading,
    isError,
    hasData,
    refetch,
    saveTodo,
    removeTodo,
  } = useResidentTodos({ from: range.from, to: range.to, todayKey });

  const type = useTypography();
  const space = useSpacing();
  // The state lines (empty day, loading, error) start where a row's title and "New to-do" do: past the circle
  // column and the gap after it.
  const stateInset = CHECKBOX + space.md;
  const styles = useThemedStyles(({ colors }) =>
    StyleSheet.create({
      scroll: {
        flex: 1,
      },
      content: {
        paddingHorizontal: HP,
        width: '100%',
        maxWidth: MAX_CONTENT_WIDTH,
        alignSelf: 'center',
      },
      // Payments/events pad the top of the frame; mobile keeps its own rhythm.
      contentDesktop: {
        paddingTop: HP,
      },
      backLink: {
        marginBottom: -4,
      },
      titleBlock: {
        paddingTop: space['2xl'],
      },
      calendar: {
        marginTop: space.xl,
      },
      columns: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: space['6xl'],
      },
      monthColumn: {
        width: MONTH_COLUMN_WIDTH,
      },
      dayColumn: {
        flex: 1,
        minWidth: 0,
      },
      // The overdue line and the day's list, under the calendar. One constant gap whether or not the line is
      // up, so nothing moves when its last row leaves and it folds away (its own space.sm above it goes with
      // it, inside the fold).
      dayBody: {
        marginTop: space.lg,
      },
      // Desktop: the day column has the title above it instead of a calendar.
      dayBodyDesktop: {
        marginTop: space.sm,
      },
      list: {
        marginTop: space.sm,
      },
      stateText: {
        ...type.subhead,
        color: colors.text.quiet,
        paddingVertical: space.md,
        paddingLeft: stateInset,
      },
      // An empty day is as tall as a row (spec §11), so the list doesn't jump when its first to-do lands.
      emptyDay: {
        minHeight: CHECKBOX + 2 * ROW_PAD_LARGE,
        justifyContent: 'center',
      },
      errorWrap: {
        alignItems: 'flex-start',
      },
      // Margin, not padding: Retry's box is its target, so it starts in the same column as the line above
      // without growing blank target to its left.
      retry: {
        marginLeft: stateInset,
      },
      retryText: {
        ...type.subhead,
        fontWeight: '600',
        color: colors.text.primary,
      },
    }),
  );

  const groups = useMemo(() => groupTodosByDateKey(todos), [todos]);
  // Incomplete only: a dot means "there is still work on this day", the same thing the home widget's
  // WeekStrip dot means. Counting finished todos too would leave the calendars disagreeing.
  const countsByKey = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [key, list] of Object.entries(groups)) {
      counts[key] = list.filter(t => !t.completed).length;
    }
    return counts;
  }, [groups]);
  const dayTodos = useMemo(() => sortTodos(groups[selectedKey] ?? []), [groups, selectedKey]);
  const dueCount = useMemo(() => dayTodos.filter(t => !t.completed).length, [dayTodos]);
  // Today's "now" line, and the heading's spoken "next at …" (spec 2026-10-07).
  const nowIndex = useMemo(
    () => nowLineIndex(dayTodos, selectedKey, todayKey, nowTimeKey),
    [dayTodos, selectedKey, todayKey, nowTimeKey],
  );
  const nextTodo = useMemo(
    () => nextTimedTodo(dayTodos, selectedKey, todayKey, nowTimeKey),
    [dayTodos, selectedKey, todayKey, nowTimeKey],
  );

  const handlePrevMonth = useCallback(() => setSelectedDate(d => addMonthsToStart(d, -1)), []);
  const handleNextMonth = useCallback(() => setSelectedDate(d => addMonthsToStart(d, 1)), []);
  const handleWeekChange = useCallback(
    (step: -1 | 1) => setSelectedDate(d => addDays(d, 7 * step)),
    [],
  );
  const handleToday = useCallback(() => setSelectedDate(now), [now]);
  const toggleMonth = useCallback(() => setMonthOpen(open => !open), []);
  const handleAdd = useCallback(() => {
    setFormTodo(null);
    setFormVisible(true);
  }, []);
  const handleEdit = useCallback((todo: ResidentTodo) => {
    setFormTodo(todo);
    setFormVisible(true);
  }, []);
  const handleCloseForm = useCallback(() => setFormVisible(false), []);
  // The modal owns try/catch; the hook maps values + scope to the right request.
  const handleSaveForm = useCallback(
    (values: TodoFormValues, scope?: EditScope) => saveTodo(formTodo, values, scope),
    [formTodo, saveTodo],
  );

  // The heading's spoken tail after "{title}, {date line}".
  const labelSuffix = `${dueCount > 0 ? `, ${dueCount} due` : ''}${
    nextTodo?.dueTime ? `, next at ${formatTime(nextTodo.dueTime)}` : ''
  }`;

  const monthGrid = (
    <MonthGrid
      monthAnchor={selectedDate}
      selectedKey={selectedKey}
      todayKey={todayKey}
      countsByKey={countsByKey}
      onSelectDay={setSelectedDate}
      onPrevMonth={handlePrevMonth}
      onNextMonth={handleNextMonth}
    />
  );

  // Always mounted: the section renders nothing when empty, and it has to stay up while its last row
  // (and then the block itself) animates out. Its space.sm above lives inside its collapse, so it goes
  // with the block; the rest of the gap under the calendar is the day body's, and stays.
  const overdue = (
    <OverdueSection
      todos={overdueTodos}
      todayKey={todayKey}
      onToggle={toggleTodo}
      onEdit={handleEdit}
      gapAbove={space.sm}
    />
  );

  const dayList = isLoading ? (
    <Text style={styles.stateText}>Loading your to-dos…</Text>
  ) : isError && !hasData ? (
    // Same rule as the widget: only a failure with NOTHING cached (no response has ever landed —
    // `todos` is just this month's slice, so it can't tell) takes over the list. A failed background
    // refetch with records in hand keeps the list, and row-level rollback is the feedback.
    <View style={styles.errorWrap}>
      <Text style={styles.stateText}>Couldn't load your to-dos.</Text>
      <TouchableOpacity
        style={styles.retry}
        onPress={refetch}
        accessibilityRole="button"
        accessibilityLabel="Retry loading to-dos"
        hitSlop={RETRY_SLOP}
      >
        <Text style={styles.retryText}>Retry</Text>
      </TouchableOpacity>
    </View>
  ) : (
    <View>
      {dayTodos.length === 0 ? (
        <View style={styles.emptyDay}>
          <Text style={styles.stateText}>Nothing planned</Text>
        </View>
      ) : (
        <View>
          {insertNowLine(
            dayTodos.map(todo => (
              <TodoRow
                key={todo.id}
                todo={todo}
                onToggle={toggleTodo}
                onEdit={handleEdit}
                late={isLate(todo, todayKey, nowTimeKey)}
              />
            )),
            nowIndex,
            undefined,
            NOW_LINE_OPTIONS,
          )}
        </View>
      )}
      <NewTodoRow onPress={handleAdd} />
    </View>
  );

  // The overdue line, then the list: the day's body on both layouts, one constant gap under the calendar.
  const dayBody = (
    <View style={[styles.dayBody, isDesktop && styles.dayBodyDesktop]}>
      {overdue}
      <View style={styles.list}>{dayList}</View>
    </View>
  );

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[
        styles.content,
        isDesktop && styles.contentDesktop,
        { paddingBottom: 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {showBackLink && <ScreenBackLink style={styles.backLink} />}
      {isDesktop ? (
        <View style={[styles.columns, styles.titleBlock]}>
          <View style={styles.monthColumn}>{monthGrid}</View>
          <View style={styles.dayColumn}>
            <DayTitle date={selectedDate} now={now} labelSuffix={labelSuffix} onToday={handleToday} />
            {dayBody}
          </View>
        </View>
      ) : (
        <>
          <View style={styles.titleBlock}>
            <DayTitle
              date={selectedDate}
              now={now}
              labelSuffix={labelSuffix}
              onToday={handleToday}
              monthToggle={{ open: monthOpen, onToggle: toggleMonth }}
            />
          </View>
          <View style={styles.calendar}>
            <Fold open={!monthOpen} animate={!reduceMotion}>
              <PlannerWeekStrip
                selectedDate={selectedDate}
                todayKey={todayKey}
                countsByKey={countsByKey}
                onSelectDay={setSelectedDate}
                onWeekChange={handleWeekChange}
              />
            </Fold>
            <Fold open={monthOpen} animate={!reduceMotion}>
              {monthGrid}
            </Fold>
          </View>
          {dayBody}
        </>
      )}
      <TodoFormModal
        visible={formVisible}
        todo={formTodo}
        initialDateKey={selectedKey}
        onSave={handleSaveForm}
        onDelete={removeTodo}
        onClose={handleCloseForm}
        now={now}
      />
    </ScrollView>
  );
};

PlannerScreen.displayName = 'PlannerScreen';
